import React, { useState } from 'react';
import { cn } from '../../lib/utils';

export const CategoryList = ({
  title,
  subtitle,
  categories,
  headerIcon,
  className,
}) => {
  const [hoveredItem, setHoveredItem] = useState(null);

  return (
    <div className={cn("w-full bg-background text-foreground p-8", className)}>
      <div className="max-w-4xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-12 md:mb-16">
          {headerIcon && (
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-orange-400 to-orange-500 mb-6 text-white">
              {headerIcon}
            </div>
          )}
          <h1 className="text-4xl md:text-5xl font-bold mb-2 tracking-tight text-slate-900">{title}</h1>
          {subtitle && (
            <h2 className="text-4xl md:text-5xl font-bold text-slate-400">{subtitle}</h2>
          )}
        </div>

        {/* Categories List */}
        <div className="space-y-3">
          {categories.map((category, index) => (
            <div
              key={category.id}
              className="relative group"
              onMouseEnter={() => setHoveredItem(category.id)}
              onMouseLeave={() => setHoveredItem(null)}
              onClick={category.onClick}
            >
              <div
                className={cn(
                  "relative overflow-hidden border bg-white rounded-xl transition-all duration-300 ease-in-out cursor-pointer",
                  // Hover state styles
                  hoveredItem === category.id
                    ? 'h-36 border-orange-500 shadow-lg shadow-orange-500/20 bg-orange-50'
                    : 'h-24 border-slate-200 hover:border-orange-300'
                )}
              >
                {/* Step Number */}
                <div className={cn(
                  "absolute top-4 left-4 w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg transition-all duration-300",
                  hoveredItem === category.id
                    ? 'bg-orange-500 text-white'
                    : 'bg-slate-100 text-slate-500'
                )}>
                  {String(index + 1).padStart(2, '0')}
                </div>

                {/* Corner brackets that appear on hover */}
                {hoveredItem === category.id && (
                  <>
                    <div className="absolute top-3 right-3 w-6 h-6">
                      <div className="absolute top-0 right-0 w-4 h-0.5 bg-orange-500" />
                      <div className="absolute top-0 right-0 w-0.5 h-4 bg-orange-500" />
                    </div>
                    <div className="absolute bottom-3 left-3 w-6 h-6">
                      <div className="absolute bottom-0 left-0 w-4 h-0.5 bg-orange-500" />
                      <div className="absolute bottom-0 left-0 w-0.5 h-4 bg-orange-500" />
                    </div>
                  </>
                )}

                {/* Content */}
                <div className="flex items-center justify-between h-full px-6 md:px-8 pl-16 md:pl-20">
                  <div className="flex-1">
                    <h3
                      className={cn(
                        "font-bold transition-colors duration-300",
                        category.featured ? 'text-2xl md:text-3xl' : 'text-xl md:text-2xl',
                        hoveredItem === category.id ? 'text-orange-600' : 'text-slate-900'
                      )}
                    >
                      {category.title}
                    </h3>
                    {category.subtitle && (
                      <p
                        className={cn(
                          "mt-1 transition-colors duration-300 text-sm md:text-base",
                           hoveredItem === category.id ? 'text-slate-700' : 'text-slate-500'
                        )}
                      >
                        {category.subtitle}
                      </p>
                    )}
                  </div>

                  {/* Icon appears on the right on hover */}
                  {category.icon && (
                    <div className={cn(
                      "text-orange-500 transition-all duration-300",
                      hoveredItem === category.id ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
                    )}>
                      {category.icon}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CategoryList;
